// src/components/SuperAdminModal.js
import React, { useState, useEffect, useMemo } from 'react';
import Modal from 'react-modal';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCrown,
  faUserSecret,
  faBuilding,
  faMoneyBillWave,
  faCheck,
  faSyncAlt,
  faExchangeAlt,
} from '@fortawesome/free-solid-svg-icons';
import styles from './SuperAdminModal.module.css';
import { useData } from '../context/DataContext';
import { useJobs } from '../context/jobContext';
import JobDetailsModal from './JobDetailsModal';
import { countryList } from '../utils/CountryList';
import Spinner from './Spinner';

// Styling for React-Select
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#2a2a30',
    borderColor: state.isFocused ? '#ffcc00' : '#555',
    color: '#fff',
    boxShadow: 'none',
    '&:hover': { borderColor: '#ffcc00' },
  }),
  singleValue: (provided) => ({ ...provided, color: '#fff' }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#1e1e24',
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#3a3a40' : '#1e1e24',
    color: '#fff',
    cursor: 'pointer',
  }),
  input: (provided) => ({ ...provided, color: '#fff' }),
};

const SuperAdminModal = () => {
  const {
    isSuperAdminModalOpen,
    setIsSuperAdminModalOpen,
    superAdminLoading,
    superAdminError,
    systemUsers,
    systemInvoices, // From Context
    fetchAllSystemUsers,
    onboardNewCompany,
    executeImpersonation,
    markInvoiceAsPaid, // From Context
    generateMonthlyInvoices, // <--- ADD THIS
    fetchSystemInvoices,
    superAdminScrapeWebsite,
    systemWalletTransactions,
    fetchSystemWalletTransactions,
    userProfile,
  } = useData();

  const {
    agentJobHistory,
    fetchAgentJobHistory,
    fetchAgentJobDetails, // We need to trigger the fetch
    dispatchMonthlyEmails, // <--- ADD THIS
    isJobDetailsModalOpen,
    setIsJobDetailsModalOpen,
    setSelectedJobDetails, // To close it
  } = useJobs();

  // Navigation State
  const [activeTab, setActiveTab] = useState('workspaces');

  // Onboarding State
  const [compName, setCompName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [compCountry, setCompCountry] = useState('US');
  const [adminPass, setAdminPass] = useState('');
  const [onboardSuccess, setOnboardSuccess] = useState('');
  const [compWebsite, setCompWebsite] = useState('');
  const [onboardStatusMsg, setOnboardStatusMsg] = useState('');

  // Impersonation State
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedJobName, setSelectedJobName] = useState(null);
  const [impersonateSuccess, setImpersonateSuccess] = useState('');

  const isMasterAdmin = userProfile?.isMasterAdmin === true;

  // Data Loading Effects
  useEffect(() => {
    if (isSuperAdminModalOpen) {
      if (activeTab === 'identities') fetchAllSystemUsers();
      if (activeTab === 'billing') {
        fetchAgentJobHistory(); // <--- Poll for dispatch jobs
      }
      if (activeTab === 'transactions') {
        fetchSystemWalletTransactions();
      }
      // Reset success messages on tab switch
      setOnboardSuccess('');
      setImpersonateSuccess('');
    }
  }, [
    isSuperAdminModalOpen,
    activeTab,
    fetchAllSystemUsers,
    fetchAgentJobHistory,
    fetchSystemWalletTransactions,
  ]);

  const targetUserOptions = useMemo(() => {
    return (systemUsers || []).map((u) => ({
      value: u.userId,
      label: `${u.userName} (${u.email}) - [${u.companyName || 'Unknown'}]`,
    }));
  }, [systemUsers]);

  // --- Handlers ---

  const handleOnboardSubmit = async (e) => {
    e.preventDefault();
    setOnboardSuccess('');
    setOnboardStatusMsg(
      'Initializing workspace, identity, AI configs, and localizing templates...',
    );

    const payload = {
      companyName: compName,
      companyCountry: compCountry,
      websiteURL: compWebsite,
      adminName,
      adminEmail,
      adminPassword: adminPass,
    };

    // Step 1: Create the Workspace
    const result = await onboardNewCompany(payload);
    console.log('[SuperAdmin] Onboard Step 1 Result:', result); // DEBUG

    if (result && result.status) {
      // WARNING: Make sure your backend apiOnboardCompany returns `companyId` in result.data!
      const newCompanyId = result.data?.companyId;
      const aiConfigured = result.data?.aiConfigured;
      // Dynamic success message
      let baseMsg = aiConfigured
        ? `Workspace created! Admin UID: ${result.data?.adminUid}. Standard AI configuration applied.`
        : `Workspace created! Admin UID: ${result.data?.adminUid}. WARNING: AI was NOT configured (missing system keys or configs).`;
      console.log(
        `[SuperAdmin] Workspace Created. ID: ${newCompanyId}, Website: ${compWebsite}`,
      );

      if (compWebsite && newCompanyId) {
        // Step 2: Scrape the website
        setOnboardStatusMsg(
          `Workspace created! Please wait while we scrape ${compWebsite} for the company profile as well as products descriptions and GTM ...`,
        );

        const scrapeResult = await superAdminScrapeWebsite(
          newCompanyId,
          compWebsite,
        );
        console.log('[SuperAdmin] Scrape Step 2 Result:', scrapeResult); // DEBUG

        if (scrapeResult.status) {
          const productCount =
            scrapeResult.data?.extracted?.products?.length || 0;
          setOnboardSuccess(
            `${baseMsg} ${productCount} products automatically imported. Please verify details in the Company and Product settings.`,
          );
        } else {
          setOnboardSuccess(
            `${baseMsg} Website scraping failed: ${scrapeResult.message}.`,
          );
        }
      } else {
        setOnboardSuccess(baseMsg);
      }

      // Reset form
      setCompName('');
      setCompCountry('US');
      setAdminName('');
      setAdminEmail('');
      setAdminPass('');
      setCompWebsite('');
      setOnboardStatusMsg('');
    } else {
      setOnboardStatusMsg(''); // Clear status if creation failed
    }
  };

  const handleImpersonateClick = async () => {
    if (!selectedUser) return;
    setImpersonateSuccess('Minting custom token... System reloading...');
    await executeImpersonation(selectedUser.value);
  };

  const handleMarkPaid = async (invoiceId) => {
    if (
      window.confirm(
        "Are you sure? This will immediately restore the user's access.",
      )
    ) {
      await markInvoiceAsPaid(invoiceId);
    }
  };

  const handleClose = () => {
    if (!superAdminLoading) setIsSuperAdminModalOpen(false);
  };

  // --- Render Functions ---

  const renderWorkspaces = () => (
    <div className={styles.panelContainer}>
      <h3>Onboard New Workspace</h3>
      <div className={styles.warningBox}>
        Creates a new isolated company tenant.
      </div>
      <form onSubmit={handleOnboardSubmit}>
        <div className={styles.formGroup}>
          <label>Company Name:</label>
          <input
            type="text"
            value={compName}
            onChange={(e) => setCompName(e.target.value)}
            required
          />
        </div>
        {/* NEW COUNTRY DROPDOWN */}
        <div className={styles.formGroup}>
          <label>Workspace Country:</label>
          <select
            value={compCountry}
            onChange={(e) => setCompCountry(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '4px',
              border: '1px solid #555',
              backgroundColor: '#2a2a30',
              color: '#fff',
              outline: 'none',
            }}
          >
            {countryList.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label>Company Website URL (Optional):</label>
          <input
            type="url"
            placeholder="https://www.example.com"
            value={compWebsite}
            onChange={(e) => setCompWebsite(e.target.value)}
          />
          <small style={{ color: '#888', marginTop: '4px' }}>
            If provided, AI will automatically populate products and company
            descriptions.
          </small>
        </div>
        <div className={styles.formGroup}>
          <label>Admin Full Name:</label>
          <input
            type="text"
            value={adminName}
            onChange={(e) => setAdminName(e.target.value)}
            required
          />
        </div>
        <div className={styles.formGroup}>
          <label>Admin Email:</label>
          <input
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            required
          />
        </div>
        <div className={styles.formGroup}>
          <label>Temporary Password:</label>
          <input
            type="password"
            value={adminPass}
            onChange={(e) => setAdminPass(e.target.value)}
            minLength="6"
            required
          />
        </div>
        <button
          type="submit"
          className={styles.actionButton}
          disabled={superAdminLoading}
        >
          Initialize Workspace
        </button>
      </form>
      {onboardStatusMsg && (
        <div
          className={styles.taskSuccess}
          style={{
            backgroundColor: '#1a3c5c',
            borderColor: '#0088ff',
            color: '#69a3f0',
          }}
        >
          <Spinner message={onboardStatusMsg} />
        </div>
      )}
      {onboardSuccess && (
        <div className={styles.taskSuccess}>{onboardSuccess}</div>
      )}
    </div>
  );

  const renderIdentities = () => (
    <div className={styles.panelContainer}>
      <h3>Impersonate Identity</h3>
      <div
        className={styles.warningBox}
        style={{
          borderColor: '#ff4d4d',
          color: '#ffb3b3',
          backgroundColor: '#330000',
        }}
      >
        <strong>WARNING:</strong> You will become this user. Your current
        session will end.
      </div>
      <div className={styles.formGroup}>
        <label>Select Target:</label>
        <Select
          options={targetUserOptions}
          value={selectedUser}
          onChange={setSelectedUser}
          styles={customSelectStyles}
          placeholder="Search users..."
          isDisabled={superAdminLoading}
        />
      </div>
      <button
        className={styles.actionButton}
        style={{ backgroundColor: '#ff4d4d', color: 'white' }}
        onClick={handleImpersonateClick}
        disabled={!selectedUser || superAdminLoading}
      >
        Assume Identity
      </button>
      {impersonateSuccess && (
        <div className={styles.taskSuccess}>{impersonateSuccess}</div>
      )}
    </div>
  );

  const renderBilling = () => {
    // --- THE UI VISIBILITY FIX ---
    // Sort all dispatch jobs by ID descending (newest first), then take the top 3.
    // This keeps the "View" button on screen after completion without cluttering the UI.
    const recentDispatchJobs = (agentJobHistory || [])
      .filter((j) => j.jobType === 'DISPATCH_INVOICES')
      .sort((a, b) => b.id - a.id)
      .slice(0, 3);

    return (
      <div>
        <JobDetailsModal
          isOpen={isJobDetailsModalOpen}
          onClose={() => setSelectedJobDetails(null)}
          jobName={selectedJobName}
        />

        <div>
          {/* --- DISPATCH MONITOR --- */}
          {recentDispatchJobs.length > 0 && (
            <div
              className={styles.warningBox}
              style={{ backgroundColor: '#1c1e29', borderColor: '#444' }}
            >
              <h4 style={{ marginTop: 0, color: '#ffcc00' }}>
                Recent Dispatch Jobs
              </h4>

              {recentDispatchJobs.map((job) => {
                const progressPct =
                  job.totalItems > 0
                    ? (job.processedItems / job.totalItems) * 100
                    : 0;
                // Make completed jobs green, failed red, pending blue
                const barColor =
                  job.status === 'failed'
                    ? '#d32f2f'
                    : job.status === 'completed'
                      ? '#1a5c1d'
                      : '#6f9aee';

                return (
                  <div
                    key={job.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '5px',
                      fontSize: '0.9rem',
                    }}
                  >
                    <div
                      style={{
                        width: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {job.jobName}
                    </div>

                    {/* Progress Bar */}
                    <div
                      style={{
                        flex: 1,
                        height: '8px',
                        background: '#333',
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${progressPct}%`,
                          height: '100%',
                          background: barColor,
                          transition: 'width 0.5s',
                        }}
                      />
                    </div>

                    <div style={{ width: '80px', textAlign: 'right' }}>
                      {job.status === 'completed'
                        ? 'Done'
                        : `${job.processedItems}/${job.totalItems}`}
                    </div>

                    {/* View Details Button */}
                    <button
                      onClick={() => {
                        setSelectedJobName(job.jobName);
                        // JobContext handles the loading states natively, just call fetch
                        fetchAgentJobDetails(job.id, job.jobType);
                      }}
                      style={{
                        background: 'none',
                        border: '1px solid #555',
                        color: '#aaa',
                        cursor: 'pointer',
                        borderRadius: '3px',
                        fontSize: '0.8em',
                        padding: '2px 8px',
                      }}
                    >
                      View
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* --- HEADER & ACTION BUTTONS --- */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px',
            }}
          >
            <h3>System Statements</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                className={styles.actionButton}
                onClick={generateMonthlyInvoices}
                style={{
                  background: '#6f9aee',
                  color: 'white',
                  padding: '8px 15px',
                  fontSize: '0.85em',
                }}
                disabled={superAdminLoading}
              >
                Generate Statements
              </button>
              <button
                className={styles.actionButton}
                onClick={(e) => {
                  e.preventDefault(); // Stop any default button behavior
                  dispatchMonthlyEmails(); // Call with NO arguments
                }}
                style={{
                  background: '#f57c00',
                  color: 'white',
                  padding: '8px 15px',
                  fontSize: '0.85em',
                }}
                disabled={superAdminLoading}
              >
                Dispatch Emails
              </button>
              <button
                className={styles.actionButton}
                onClick={() => {
                  const results = fetchSystemInvoices();
                  console.log('Results FetchSystemInvoices: ', results);
                }}
                style={{ fontSize: '0.8em', padding: '8px 15px' }}
                disabled={superAdminLoading}
              >
                <FontAwesomeIcon icon={faSyncAlt} /> Refresh
              </button>
            </div>
          </div>

          {/* --- TABLE --- */}
          {!systemInvoices || systemInvoices.length === 0 ? (
            <div
              style={{ textAlign: 'center', color: '#888', padding: '20px' }}
            >
              No invoices found.
            </div>
          ) : (
            <table className={styles.billingTable}>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Period</th>
                  <th>Amount</th>
                  <th>User / Company</th>
                  <th>Created</th>
                  <th>Paid On</th>
                  <th>Emailed</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {systemInvoices.map((inv) => (
                  <tr key={inv.invoiceId}>
                    <td>
                      <span
                        className={`${styles.badge} ${inv.status === 'PAID' ? styles.paid : styles.unpaid}`}
                      >
                        {inv.status}
                      </span>
                    </td>
                    <td>{inv.billing_month}</td>
                    <td>${inv.amount_usd}</td>
                    <td>
                      <div>{inv.userName}</div>
                      <div style={{ fontSize: '0.8em', color: '#888' }}>
                        {inv.companyName || 'N/A'}
                      </div>
                    </td>
                    <td>{new Date(inv.created_at).toLocaleDateString()}</td>
                    <td>
                      {inv.paid_at
                        ? new Date(inv.paid_at).toLocaleDateString()
                        : '-'}
                    </td>
                    <td>
                      {inv.emailed_at ? (
                        <span style={{ color: '#69f0ae' }}>✓ Sent</span>
                      ) : (
                        <span style={{ color: '#888' }}>Pending</span>
                      )}
                    </td>
                    <td>
                      {inv.status === 'UNPAID' ? (
                        <button
                          className={styles.markPaidBtn}
                          onClick={() => handleMarkPaid(inv.invoiceId)}
                        >
                          <FontAwesomeIcon icon={faCheck} /> Mark Paid
                        </button>
                      ) : (
                        <span style={{ color: '#69f0ae', fontSize: '0.85em' }}>
                          Settled
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  const renderTransactions = () => {
    return (
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px',
          }}
        >
          <h3>Global Wallet Ledger</h3>
          <button
            className={styles.actionButton}
            onClick={fetchSystemWalletTransactions}
            style={{ fontSize: '0.8em', padding: '8px 15px' }}
            disabled={superAdminLoading}
          >
            <FontAwesomeIcon icon={faSyncAlt} /> Refresh
          </button>
        </div>
        <table className={styles.billingTable}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Workspace</th>
              <th>Type</th>
              <th>Amount</th>
              <th>User</th>
              <th>Stripe Intent</th>
            </tr>
          </thead>
          <tbody>
            {systemWalletTransactions.map((tx) => (
              <tr key={tx.transactionId}>
                <td>{new Date(tx.created_at).toLocaleString()}</td>
                <td>{tx.companyName}</td>
                <td>
                  <span
                    className={`${styles.txBadge} ${styles[tx.transaction_type]}`}
                  >
                    {tx.transaction_type.replace(/_/g, ' ')}
                  </span>
                </td>
                <td
                  style={{
                    color:
                      tx.transaction_type === 'LLM_USAGE'
                        ? '#ff8a80'
                        : '#69f0ae',
                    fontWeight: 'bold',
                  }}
                >
                  {tx.transaction_type === 'LLM_USAGE' ? '-' : '+'}$
                  {Math.abs(parseFloat(tx.amount_usd)).toFixed(2)}
                </td>
                <td>{tx.userName || 'System'}</td>
                <td style={{ fontSize: '0.85em', color: '#888' }}>
                  {tx.stripe_payment_intent || 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!isSuperAdminModalOpen) return null;

  return (
    <Modal
      isOpen={isSuperAdminModalOpen}
      onRequestClose={handleClose}
      className={styles.modalContent}
      overlayClassName={styles.modalOverlay}
      shouldCloseOnOverlayClick={false}
    >
      {superAdminLoading && <Spinner message="Processing..." />}

      {/* --- Header & Tabs --- */}
      <div className={styles.modalHeader}>
        <div className={styles.titleRow}>
          <h2>
            <FontAwesomeIcon icon={faCrown} /> SuperAdmin
          </h2>
          <button
            onClick={handleClose}
            className={`${styles.actionButton} ${styles.closeButton}`}
            style={{ padding: '5px 15px' }}
          >
            Close
          </button>
        </div>
        <div className={styles.tabContainer}>
          <button
            className={`${styles.tab} ${activeTab === 'workspaces' ? styles.active : ''}`}
            onClick={() => setActiveTab('workspaces')}
          >
            <FontAwesomeIcon icon={faBuilding} /> Workspaces
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'identities' ? styles.active : ''}`}
            onClick={() => setActiveTab('identities')}
          >
            <FontAwesomeIcon icon={faUserSecret} /> Identities
          </button>
          {/* ONLY RENDER THESE TABS FOR THE CEO */}
          {isMasterAdmin && (
            <>
              <button
                className={`${styles.tab} ${activeTab === 'billing' ? styles.active : ''}`}
                onClick={() => setActiveTab('billing')}
              >
                <FontAwesomeIcon icon={faMoneyBillWave} /> Billing
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'transactions' ? styles.active : ''}`}
                onClick={() => setActiveTab('transactions')}
              >
                <FontAwesomeIcon icon={faExchangeAlt} /> Wallet Transactions
              </button>
            </>
          )}
        </div>
      </div>

      {/* --- Global Error --- */}
      {superAdminError && (
        <div className={styles.taskError} style={{ margin: '0' }}>
          {superAdminError}
        </div>
      )}

      {/* --- Body Content --- */}
      <div className={styles.modalBody}>
        {activeTab === 'workspaces' && renderWorkspaces()}
        {activeTab === 'identities' && renderIdentities()}
        {activeTab === 'billing' && renderBilling()}
        {activeTab === 'transactions' && renderTransactions()}
      </div>
    </Modal>
  );
};

export default SuperAdminModal;
